import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ZonalGrossMarginFormMemo } from "../components/ZonalGrossMarginForm";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchZonalGrossMarginDetailHandler,
  updateZonalGrossMarginHandler,
} from "./schema/handler-functions";
import { ZonalGrossMarginDetail } from "@/services/zonal-gross-margin.service";
import { ZonalGrossMarginCreationForm, zonalGrossMarginCreationSchema } from "../create/schema/zonal-gross-margin-creation.schema";
import {
  calculateImplantationCost,
  calculateInvestmentTotal,
  getDefaultZonalGrossMarginFormValues,
  mapZonalGrossMarginDetailToFormValues,
} from "../utils/zonal-gross-margin-converters";
import { hasEmptyZonalGrossMarginFormValues } from "../utils/zonal-gross-margin-form";
import { fetchZonalGrossMarginOptionsHandler } from "../schema/options-handler-functions";

export const ZonalGrossMarginDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasWriteAccess, fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingZonalGrossMargin, setLoadingZonalGrossMargin] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [zonalGrossMargin, setZonalGrossMargin] =
    useState<ZonalGrossMarginDetail | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [grains, setGrains] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ZonalGrossMarginCreationForm>({
    resolver: zodResolver(zonalGrossMarginCreationSchema),
    defaultValues: getDefaultZonalGrossMarginFormValues(),
  });

  const watchedValues = useWatch({ control });
  const prevInvestmentTotal = useRef<string>("");

  useEffect(() => {
    setIsActive(true);
  }, []);

  const implantationCost = useMemo(() => {
    return calculateImplantationCost(watchedValues);
  }, [
    watchedValues.precioAgroquimicos,
    watchedValues.precioFertilizantes,
    watchedValues.precioLabores,
    watchedValues.precioSeguros,
    watchedValues.precioSemillas,
  ]);

  const investmentTotal = useMemo(() => {
    return calculateInvestmentTotal(watchedValues);
  }, [
    watchedValues.precioAgroquimicos,
    watchedValues.precioFertilizantes,
    watchedValues.precioLabores,
    watchedValues.precioSeguros,
    watchedValues.precioSemillas,
    watchedValues.precioAlquiler,
  ]);

  const prevImplantationCost = useRef<string>("");

  useEffect(() => {
    const nextValue = String(Number(implantationCost.toFixed(2)));
    if (prevImplantationCost.current === nextValue) return;

    prevImplantationCost.current = nextValue;
    setValue("costoImplantacion", nextValue, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [implantationCost, setValue]);

  useEffect(() => {
    const nextValue = String(Number(investmentTotal.toFixed(2)));
    if (prevInvestmentTotal.current === nextValue) return;

    prevInvestmentTotal.current = nextValue;
    setValue("inversionTotal", nextValue, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [investmentTotal, setValue]);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [summaryValues, setSummaryValues] = useState<ZonalGrossMarginCreationForm | null>(null);

  const selectedGrainName = useMemo(() => {
    const selectedGrainId = Number((summaryValues?.idGranoProductivo ?? [])[0]);
    return grains.find((grain) => grain.id === selectedGrainId)?.nombre ?? "-";
  }, [grains, summaryValues?.idGranoProductivo]);
  const handleConfirmAccept = async () => {
    if (!summaryValues) return;
    await onSubmit(summaryValues);
  };

  const handleOpenConfirm = async (values: ZonalGrossMarginCreationForm) => {
    setSummaryValues(values);
    setOpenConfirm(true);
  };
  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const options = await fetchZonalGrossMarginOptionsHandler();
      setZones(options.zones);
      setGrains(options.grains);
      setCampaigns(options.campaigns);
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchZonalGrossMargin = async () => {
    if (!id) return;
    await fetchZonalGrossMarginDetailHandler({
      setLoading: setLoadingZonalGrossMargin,
      setZonalGrossMargin,
      navigate,
      fetchPermissions,
      id,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchOptions();
      fetchZonalGrossMargin();
    }
  }, [isActive, id]);

  useEffect(() => {
    if (zonalGrossMargin) {
      reset(mapZonalGrossMarginDetailToFormValues(zonalGrossMargin));
    }
  }, [zonalGrossMargin, reset]);

  const handleBack = () => {
    navigate(AppRoutes.ZONAL_GROSS_MARGINS);
  };

  const onSubmit = async (data: ZonalGrossMarginCreationForm) => {
    if (!id) return;
    setLoadingUpdate(true);
    try {
      await updateZonalGrossMarginHandler({
        body: data,
        id,
        navigate,
        fetchPermissions,
        onSuccess: () => {
          navigate(AppRoutes.ZONAL_GROSS_MARGINS);
        },
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyZonalGrossMarginFormValues(watchedValues);
  }, [watchedValues]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-testid="zonal-gross-margin-detail-page"
      overflowY="auto"
      maxH="calc(100vh - 32px)"
      paddingRight="4px"
    >
      <SectionHeader
        title="Editar Margen Bruto Zonal"
        cancelButtonLabel="Cancelar"
        handleCancel={handleBack}
        handleAdd={handleSubmit(handleOpenConfirm)}
        loading={loadingUpdate}
        showButtons={hasWriteAccess(PermissionCode.ZONAL_GROSS_MARGIN)}
        addButtonLabel="Completar y enviar"
        disableAdd={hasNoFilledValues || loadingUpdate || loadingOptions}
      />

      <ZonalGrossMarginFormMemo
        control={control}
        errors={errors}
        zones={zones}
        grains={grains}
        campaigns={campaigns}
        isActive={isActive}
        loading={loadingZonalGrossMargin}
        loadingOptions={loadingOptions}
      />

      <Suspense>
        <ZonalGrossMarginConfirmModalDynamic
          open={openConfirm}
          setOpen={setOpenConfirm}
          data={summaryValues}
          onAccept={handleConfirmAccept}
          loading={loadingUpdate}
          grainName={selectedGrainName}
        />
      </Suspense>
    </Flex>
  );
};



const ZonalGrossMarginConfirmModalDynamic =
  lazy(() => import("../components/ZonalGrossMarginConfirmationModal").then(module => ({ default: module.ZonalGrossMarginConfirmationModal })))

